import dayjs from 'dayjs';
import { useState } from 'react';

import { showDrillDownContextMenu } from '../../conversation/overlay/show-drill-down-context-menu';
import { useFilteredPostsNavigation } from '../../filtered-posts/hook/use-filtered-posts-navigation';
import { useFilteredProfilesNavigation } from '../../filtered-profiles/hook/use-filtered-profiles-navigation';
import { usePerformance } from '../../shared/hooks/use-performance';
import { calculateDateRangeFromBar } from '../helpers/calculate-date-range-from-bar';
import { useAddFilterToAskSyncly } from '../hooks/use-add-filter-to-ask-syncly';
import { useSelectedPeriod } from '../hooks/use-selected-period';
import { ChartPeriodSelector } from './chart-period-selector';
import { EngagementMetrics } from './engagement-metrics';

import { platformColorPalette, platformOptions } from '@/entities/public-filter/constants';
import { ChartLoader } from '@/shared/components/chart-loader';
import type { ChartDataPoint } from '@/shared/components/charts/stack-chart.types';
import { SectionHeader } from '@/shared/components/section-header';
import { Card, CardContent, CardHeader } from '@/shared/components/shadcn/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import { useSharedDateRange } from '@/shared/hooks/use-shared-date-range';
import { chartPeriodToDayjsUnit } from '@/shared/utils/date/chart-period-to-dayjs-unit';
import { StackBarChart } from '../../../../shared/components/charts/stack-chart';
import { usePublicFilter } from '../../shared/public-filter/use-public-filter';
import { useEngagementStackChart } from '../hooks/use-engagement-stack-chart';

type Metric = 'total' | 'like' | 'comment' | 'shared';

export function EngagementSection({ fetchEnabled }: { fetchEnabled: boolean }) {
  const { query: selectedQuery } = useSelectedSavedQuery();
  const { dateRange } = useSharedDateRange();
  const [selectedMetric, setSelectedMetric] = useState<Metric>('total');
  const [selectedPeriod, setSelectedPeriod] = useSelectedPeriod({ dateRange });
  const { publicFilter } = usePublicFilter(selectedQuery.id);
  const navigateToFilteredPosts = useFilteredPostsNavigation();
  const navigateToFilteredProfiles = useFilteredProfilesNavigation();
  const addFilterToAskSyncly = useAddFilterToAskSyncly();

  const { data, isFetching } = usePerformance({
    period: selectedPeriod,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    queryId: selectedQuery.id,
    enabled: fetchEnabled,
    publicFilter,
  });

  const { chartData, chartConfig } = useEngagementStackChart({
    trends:
      data?.trends.sort((a, b) => {
        const aOrder = platformOptions.find((o) => o.value === a.platform)?.order || 0;
        const bOrder = platformOptions.find((o) => o.value === b.platform)?.order || 0;
        return bOrder - aOrder;
      }) || [],
    selectedMetric,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    dateUnit: chartPeriodToDayjsUnit(selectedPeriod),
    colorPalette: platformColorPalette,
  });

  const handleBarClick = async (dataPoint: ChartDataPoint, event: React.MouseEvent) => {
    const action = await showDrillDownContextMenu({
      position: { x: event.clientX, y: event.clientY },
      menus: ['view-posts', 'view-profiles', 'ask-syncly'],
    });

    if (action === 'close') return;

    const { startDate, endDate } = calculateDateRangeFromBar(dataPoint.date, selectedPeriod);

    // Format dates for URL
    const startDateStr = dayjs(startDate).format('YYYY-MM-DD');
    const endDateStr = dayjs(endDate).format('YYYY-MM-DD');

    switch (action) {
      case 'view-posts':
        await navigateToFilteredPosts({
          filter: {},
          dimension: 'default', // Use default dimension for date-based filtering without specific filters
          dateRange: {
            startDate: startDateStr,
            endDate: endDateStr,
          },
        });
        break;
      case 'view-profiles':
        await navigateToFilteredProfiles({
          filter: {},
          dimension: 'default', // Use default dimension for date-based filtering without specific filters
          dateRange: {
            startDate: startDateStr,
            endDate: endDateStr,
          },
        });
        break;
      case 'ask-syncly':
        addFilterToAskSyncly({
          dateRange: {
            startDate: startDateStr,
            endDate: endDateStr,
          },
          filter: null,
        });
        break;
    }
  };

  return (
    <div>
      <SectionHeader
        title="Engagement"
        description="Understand how audience engage with your contents"
      />
      <div className="p-6">
        <Card className="mb-8 border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800">
          <CardHeader className="gap-0">
            <div className="flex items-center justify-between">
              <Select
                value={selectedMetric}
                onValueChange={(value) => setSelectedMetric(value as Metric)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="total">Total engagement</SelectItem>
                  <SelectItem value="view">Views</SelectItem>
                  <SelectItem value="like">Likes</SelectItem>
                  <SelectItem value="comment">Comments</SelectItem>
                  <SelectItem value="shared">Shares</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-3">
                <ChartPeriodSelector value={selectedPeriod} onChange={setSelectedPeriod} />
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <div className="relative h-100">
              <StackBarChart
                chartData={chartData}
                chartConfig={chartConfig}
                dateUnit={chartPeriodToDayjsUnit(selectedPeriod)}
                onBarClick={handleBarClick}
              />
              {isFetching && <ChartLoader className="absolute" />}
            </div>
            <EngagementMetrics
              totalEngagements={data?.totalEngagements || 0}
              totalLikes={data?.totalLikes || 0}
              totalComments={data?.totalComments || 0}
              totalShares={data?.totalShares || 0}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
